import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { heading, pillAction, pressable, focusRing } from '../../../styles/visualLanguage';

/**
 * AdvisoryCalloutState
 *
 * The calm-info / failure-with-retry panel shared across advisory call
 * sites (list-denied, list-failed, detail-denied/absent/channelFailed).
 *
 * The prop shape deliberately mirrors `mapAdvisoryErrorCode`'s
 * `{ i18nKey, tone, retryable }` output so callers pass its result straight
 * through instead of re-deriving tone or retry policy locally.
 *
 * Scope note: this component is deliberately local under
 * `src/pages/Advisory/components/`. Generalising it into a project-wide
 * shared terminal state is a separately tracked, not-yet-done job — it
 * must not move to `src/components/` yet.
 *
 * The outer element's `role` is conditional on `tone`, mirroring the
 * `showRetry` gate below. `role="alert"` on the
 * `failure` tone is WAI-ARIA's assertive, unsolicited-interruption role —
 * appropriate because a failure was not requested and the user needs to
 * know immediately. `role="status"` with `aria-live="polite"` on the `info`
 * tone is the non-interruptive counterpart — appropriate for a calm,
 * expected terminal (no match, not subscribed) that should be announced
 * without stealing the user's attention. This also brings the component in
 * line with the repo-wide convention MUI's own `Alert` primitive already
 * sets: it defaults `role` to `"alert"` (`@mui/material/Alert/Alert.js`),
 * even though this is a bespoke full-page treatment that does not use it.
 */
export interface AdvisoryCalloutStateProps {
  tone: 'info' | 'failure';
  headingKey: string;
  bodyKey: string;
  onRetry?: () => void;
}

export function AdvisoryCalloutState({
  tone,
  headingKey,
  bodyKey,
  onRetry,
}: AdvisoryCalloutStateProps) {
  const { t } = useUntypedTranslation();

  // The structural guarantee lives here, on the render, not on any call
  // site: `tone === 'info'` never renders a button even if `onRetry` is
  // passed. A `denied` channel (`permission-denied`, `retryable: false`)
  // must never offer a retry that can never succeed against an undeployed
  // Firestore rule.
  const showRetry = tone === 'failure' && typeof onRetry === 'function';

  return (
    <Box
      data-testid="advisory-callout-state"
      role={tone === 'failure' ? 'alert' : 'status'}
      aria-live={tone === 'failure' ? undefined : 'polite'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: 6,
        px: 2,
      }}
    >
      {tone === 'info' ? (
        // theme.palette.text.disabled resolves to colors.snowTertiary
        // (`--snow-3`, tokens.css:50), an ON-DARK token — the same defect
        // fixed for the row meta line, applied here to the one other
        // advisory surface that read it. The ink read below is already a
        // `colors.*` entry, no new colour is introduced. This icon is the
        // largest element on screen in the `denied` state, the default
        // outcome in every deployed environment. `fontSize: 48` is
        // unchanged — no prototype token exists for icon sizing.
        <InfoOutlinedIcon sx={theme => ({ fontSize: 48, color: theme.colors.inkTertiary })} />
      ) : (
        <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
      )}
      <Typography variant="h6" sx={[heading, { mt: 2 }]}>
        {t(headingKey)}
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
        {t(bodyKey)}
      </Typography>
      {showRetry ? (
        <Button
          variant="outlined"
          sx={[pillAction, pressable, focusRing, { mt: 3 }]}
          onClick={onRetry}
        >
          {t('advisory:list.failed.retry')}
        </Button>
      ) : null}
    </Box>
  );
}

export default AdvisoryCalloutState;
